import React, { useState } from "react";
import {
  PartyPopper,
  GraduationCap,
  BadgeCheck,
  Building,
  Star,
  Cpu,
  Briefcase,
  Stethoscope,
  Shield,
  Globe2,
  X,
} from "lucide-react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import whatsappIcon from "../../assets/icons8-whatsapp-48.png";

const countries = ["Canada", "Germany", "USA", "UK", "Australia", "Europe"];

const trendingPrograms = [
  {
    title: "Masters in Computer Science (AI & ML)",
    country: "Canada",
    tag: "STEM • High Demand",
    intakes: "Jan / May / Sep",
    subtitle: "Top tech roles • Strong PSW",
    desc: "Deep learning, ML, and software engineering for global tech careers.",
    universities: ["University of Toronto", "UBC"],
  },
  {
    title: "Master of Business Administration (Global MBA)",
    country: "UK",
    tag: "Management",
    intakes: "Jan / Apr / Sep",
    subtitle: "Leadership • Global exposure",
    desc: "Strategic management, leadership, and consulting with diverse cohorts.",
    universities: ["London Business School", "Warwick"],
  },
  {
    title: "Data Science & Analytics",
    country: "Germany",
    tag: "High Demand • No Tuition Fees",
    intakes: "Mar / Oct",
    subtitle: "Analytics • Big Data • ML",
    desc: "Hands-on analytics, statistics and ML in Europe’s industrial hub.",
    universities: ["TU Munich", "RWTH Aachen"],
  },
  {
    title: "Bachelor of Business Administration (BBA)",
    country: "Australia",
    tag: "Undergraduate",
    intakes: "Feb / Jul",
    subtitle: "Foundation for business careers",
    desc: "Core business, marketing and finance for early-stage careers.",
    universities: ["Monash University", "UNSW"],
  },
  {
    title: "Masters in Public Health (MPH)",
    country: "USA",
    tag: "Healthcare",
    intakes: "Jan / Aug",
    subtitle: "Public health • Policy • Impact",
    desc: "Epidemiology, policy and health management with strong global relevance.",
    universities: ["Johns Hopkins", "Harvard"],
  },
  {
    title: "Masters in Cybersecurity",
    country: "Ireland",
    tag: "STEM • Work Visa Friendly",
    intakes: "Jan / Sep",
    subtitle: "Security • Networks • Cloud",
    desc: "Advanced security engineering, ethical hacking and risk management.",
    universities: ["Trinity College Dublin", "UCD"],
  },
  {
    title: "MSc in International Marketing",
    country: "UK",
    tag: "Business",
    intakes: "Jan / Sep",
    subtitle: "Brand • Strategy • Digital",
    desc: "Brand strategy, consumer behaviour and digital marketing campaigns.",
    universities: ["King’s College London", "Leeds"],
  },
  {
    title: "Automotive Engineering (EV/Hybrid Tech)",
    country: "Germany",
    tag: "Engineering • High Growth",
    intakes: "Mar / Oct",
    subtitle: "EV • Mobility • Innovation",
    desc: "Future mobility, EV systems and automotive R&D in Germany.",
    universities: ["TU Berlin", "Stuttgart"],
  },
  {
    title: "Masters in Cloud Computing",
    country: "New Zealand",
    tag: "Tech • Post-study Work Visa",
    intakes: "Feb / Jul",
    subtitle: "Cloud • DevOps • Infra",
    desc: "Cloud architecture, DevOps and scalable systems with PSW opportunities.",
    universities: ["University of Auckland", "Victoria Wellington"],
  },
  {
    title: "Biomedical Engineering",
    country: "USA",
    tag: "STEM • Healthcare",
    intakes: "Aug / Jan",
    subtitle: "MedTech • Devices • R&D",
    desc: "Medical devices, imaging and healthcare innovation.",
    universities: ["MIT", "UC San Diego"],
  },
];

const getProgramIcon = (title, tag) => {
  const t = (title + " " + tag).toLowerCase();
  if (t.includes("computer") || t.includes("ai") || t.includes("data"))
    return <Cpu size={18} />;
  if (t.includes("mba") || t.includes("business") || t.includes("marketing"))
    return <Briefcase size={18} />;
  if (t.includes("health") || t.includes("biomedical"))
    return <Stethoscope size={18} />;
  if (t.includes("cyber") || t.includes("security"))
    return <Shield size={18} />;
  return <Globe2 size={18} />;
};

export default function HeroSection() {
  const [selectedCountry, setSelectedCountry] = useState("Canada");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const { scrollYProgress } = useScroll();

  const headingY = useTransform(scrollYProgress, [0, 0.3], [0, -24]);
  const statsY = useTransform(scrollYProgress, [0, 0.3], [0, -16]);
  const glowTopY = useTransform(scrollYProgress, [0, 0.3], [0, 30]);
  const glowBottomY = useTransform(scrollYProgress, [0, 0.3], [0, -20]);
  const dividerScaleX = useTransform(scrollYProgress, [0, 0.15], [0.2, 1]);

  return (
    <>
      {/* Top Banner */}
      <div className="bg-primary text-white py-2 px-4 flex items-center justify-center gap-2 text-sm sm:text-base">
        <span>Experience personalized guidance and explore global opportunities</span>
        <PartyPopper size={16} />
        <HashLink to="#contact">
          <button className="ml-2 px-4 py-1 border border-white rounded-lg hover:bg-white hover:text-primary transition font-semibold shadow">
            Book a Visit
          </button>
        </HashLink>
      </div>

      {/* Full-Width Video + Trust Logos */}
      <section className="relative bg-black">
        <div className="max-w-7xl mx-auto overflow-hidden rounded-b-3xl">
          <iframe
            className="w-full aspect-video rounded-b-3xl"
            src="https://www.youtube.com/embed/xZjeWippxVA?autoplay=1&mute=1&loop=1&controls=0&playlist=xZjeWippxVA"
            allow="autoplay; encrypted-media; picture-in-picture"
          ></iframe>
        </div>

        {/* Trust logos row */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-center gap-3 text-[11px] sm:text-xs md:text-sm text-white/70">
          <span>Trusted by leading global partners:</span>
          {["British Council", "IDP", "ETS", "Pearson", "ICEF"].map((name) => (
            <span
              key={name}
              className="px-3 py-1 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm"
            >
              {name}
            </span>
          ))}
        </div>
      </section>

      {/* Animated Divider */}
      <motion.div
        className="h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent max-w-3xl mx-auto"
        style={{ scaleX: dividerScaleX, originX: 0.5 }}
      />

      {/* HERO TEXT + CTA + STATS */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F9FAFB] to-[#EEF1F6] py-16 sm:py-20">
        {/* Background glows with parallax */}
        <motion.div
          className="absolute top-0 left-[-120px] w-[450px] h-[450px] bg-primary/10 blur-[160px] rounded-full"
          style={{ y: glowTopY }}
        />
        <motion.div
          className="absolute bottom-0 right-[-100px] w-[400px] h-[400px] bg-primary-brown/10 blur-[150px] rounded-full"
          style={{ y: glowBottomY }}
        />

        {/* Floating particles */}
        <motion.div
          className="absolute top-24 left-1/4 w-24 h-24 bg-primary/10 blur-3xl rounded-full pointer-events-none"
          animate={{ y: [0, -12, 0], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "mirror" }}
        />
        <motion.div
          className="absolute top-40 right-1/4 w-20 h-20 bg-primary-brown/10 blur-2xl rounded-full pointer-events-none"
          animate={{ y: [0, 10, 0], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 12, repeat: Infinity, repeatType: "mirror" }}
        />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ y: headingY }}
            className="max-w-4xl mx-auto text-center space-y-6"
          >
            <h1 className="text-[2.8rem] sm:text-[3.5rem] md:text-[4rem] font-semibold leading-tight tracking-tight text-primary">
              We are bringing
              <br />
              <span className="font-bold bg-gradient-to-r from-primary to-primary-brown bg-clip-text text-transparent">
                World-Class Learning
              </span>
              <br />
              closer than ever
            </h1>

            {/* Trust Ribbon */}
            <div className="flex items-center justify-center gap-4 text-primary-dark text-xs sm:text-sm font-medium">
              <span className="flex items-center gap-1">
                <Star size={16} className="text-yellow-500" /> 4.9/5 Google Rating
              </span>
              <span className="w-1 h-1 bg-primary rounded-full" />
              <span>ICEF Accredited</span>
              <span className="w-1 h-1 bg-primary rounded-full" />
              <span>120+ University Partners</span>
            </div>

            {/* Subtext */}
            <p className="text-lg sm:text-xl text-primary-dark max-w-2xl mx-auto leading-relaxed font-light">
              Explore global educational pathways with expert guidance tailored for your success.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-5 pt-2">
              <div className="text-center">
                <HashLink to="#contact">
                  <button className="px-8 py-3 bg-primary text-white text-lg font-medium rounded-xl shadow-lg hover:-translate-y-1 transition-all">
                    Book Free Consultation
                  </button>
                </HashLink>
                <p className="text-xs mt-1 text-primary-dark">No charges • Instant callback</p>
              </div>

              {/* <div className="text-center">
  <Link to="/eligibility">
    <button className="px-8 py-3 bg-white text-primary text-lg font-medium border border-primary rounded-xl shadow-md hover:bg-primary hover:text-white transition-all">
      Get Eligibility Assessment
    </button>
  </Link>
  <p className="text-xs mt-1 text-primary-dark">
    Free • Personalized • Delivered via email
  </p>
</div> */}

            </div>
          </motion.div>

          {/* STATS GRID with slight parallax */}
          <motion.div
            style={{ y: statsY }}
            className="max-w-4xl mx-auto grid grid-cols-3 gap-6 mt-14"
          >
            {[
              { icon: <GraduationCap size={24} />, value: "10000+", label: "Students Guided" },
              { icon: <BadgeCheck size={24} />, value: "95%", label: "Visa Success" },
              { icon: <Building size={24} />, value: "200+", label: "Top Universities" },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.4 }}
                className="p-6 bg-white/80 backdrop-blur-xl rounded-2xl text-center shadow-sm"
              >
                <div className="w-10 h-10 mx-auto flex items-center justify-center bg-primary/10 rounded-full">
                  {stat.icon}
                </div>
                <p className="text-2xl font-semibold text-primary mt-2">{stat.value}</p>
                <p className="text-sm text-primary-dark">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* DESTINATION SCROLLER – smart tabs */}
          <div className="max-w-4xl mx-auto mt-12">
            <p className="text-primary-brown font-semibold text-left mb-2">
              Choose your destination:
            </p>
            <div className="flex gap-3 overflow-x-auto py-1 scrollbar-hide">
  {countries.map((country) => {
    const active = selectedCountry === country;

    return (
      <Link
        key={country}
        to={`/country-overview/${country.toLowerCase()}`}
        onClick={() => setSelectedCountry(country)}
        className="whitespace-nowrap"
      >
        <button
          className={`px-5 py-2 rounded-full text-sm font-medium border backdrop-blur-md transition-all
          ${
            active
              ? "bg-primary text-white shadow-[0_0_25px_rgba(0,76,153,0.45)] border-primary"
              : "bg-white/60 text-primary border-primary/10 hover:bg-white hover:shadow-md"
          }`}
        >
          {country}
        </button>
      </Link>
    );
  })}
</div>

          </div>
        </div>
      </section>

      {/* TRENDING PROGRAMS */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-primary">
                Trending Programs
              </h2>
              <p className="text-xs sm:text-sm text-primary-dark mt-1">
                In-demand programs students are choosing right now.
              </p>
            </div>
            {/* <Link
              to="/programs"
              className="hidden sm:inline-flex text-xs sm:text-sm text-primary font-semibold hover:underline"
            >
              View all programs →
            </Link> */}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex gap-5 overflow-x-auto scrollbar-hide -mx-2 px-2"
          >
            {trendingPrograms.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                whileHover={{ y: -6 }}
                className="min-w-[260px] bg-gradient-to-b from-[#F9FBFF] to-[#EEF2FF] rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all border border-primary/10 cursor-pointer"
                onClick={() => setSelectedProgram(p)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-semibold px-3 py-1 rounded-full bg-white/70 text-primary uppercase tracking-wide">
                    {p.tag}
                  </span>
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-primary">
                    {getProgramIcon(p.title, p.tag)}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-primary mt-3 leading-snug">
                  {p.title}
                </h3>
                <p className="text-xs text-primary-dark">{p.country}</p>

                <p className="text-[11px] text-primary-dark mt-2">
                  {p.subtitle}
                </p>

                <div className="h-[1px] bg-primary/10 my-3" />
                <p className="text-xs text-primary-dark mb-4">
                  <span className="font-medium text-primary">Intakes:</span> {p.intakes}
                </p>

                <button className="w-full text-xs font-semibold text-primary hover:underline flex items-center justify-center gap-1">
                  Get Program Guide <span>→</span>
                </button>
              </motion.div>
            ))}
          </motion.div>

          {/* Mobile "View all" */}
          <div className="mt-6 text-center sm:hidden">
            <Link
              to="/programs"
              className="inline-flex text-xs text-primary font-semibold hover:underline"
            >
              View all programs →
            </Link>
          </div>
        </div>
      </section>

      {/* PROGRAM MODAL */}
      <AnimatePresence>
        {selectedProgram && (
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 18 }}
              className="bg-white rounded-2xl p-7 w-[90%] max-w-md shadow-xl relative"
            >
              <button
                onClick={() => setSelectedProgram(null)}
                className="absolute top-4 right-4 text-gray-500 hover:text-black"
              >
                <X size={20} />
              </button>

              <h3 className="text-xl font-semibold text-primary">
                {selectedProgram.title}
              </h3>
              <p className="text-sm text-primary-dark mt-1">
                {selectedProgram.country}
              </p>

              <div className="mt-3 text-[11px] px-3 py-1 w-fit rounded-full bg-primary/10 text-primary font-semibold uppercase tracking-wide">
                {selectedProgram.tag}
              </div>

              <p className="text-sm text-primary-dark mt-4 leading-relaxed">
                {selectedProgram.desc}
              </p>

              <p className="text-sm text-primary mt-4">
                <strong>Intakes:</strong> {selectedProgram.intakes}
              </p>

              {selectedProgram.universities && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-primary mb-1">
                    Top Universities:
                  </p>
                  <ul className="text-xs text-primary-dark list-disc list-inside space-y-1">
                    {selectedProgram.universities.map((u) => (
                      <li key={u}>{u}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3">
                {/* <button className="w-full py-2 bg-primary text-white rounded-lg font-medium text-sm">
                  Download Program Guide (PDF)
                </button> */}
                <HashLink to="#contact" onClick={() => setSelectedProgram(null)}>
                  <button className="w-full py-2 bg-white border border-primary text-primary rounded-lg font-medium text-sm">
                    Talk to a Counselor
                  </button>
                </HashLink>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp Glass CTA */}
      <a
        href="https://wa.me/919825772794"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-5 bottom-5 sm:right-8 sm:bottom-8 z-50"
        aria-label="Chat with us on WhatsApp"
      >
        <div className="relative">
          <motion.div
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-xl border border-white/40 shadow-lg flex items-center justify-center"
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatType: "mirror" }}
          >
            <img src={whatsappIcon} alt="WhatsApp" className="w-7 h-7 sm:w-8 sm:h-8" />
          </motion.div>
        </div>
      </a>
    </>
  );
}


// function WhatsAppCTA() {
//   return (
//     <a
//       href="https://wa.me/919825772794"
//       target="_blank"
//       rel="noopener noreferrer"
//       className="fixed left-10 max-md:left-5 bottom-10 transform bg-green-500 text-white p-3 rounded-lg shadow-lg hover:bg-green-600 transition-all duration-300 ease-in-out group z-50"
//       aria-label="Contact us on WhatsApp"
//     >
//       <div className="flex items-center">
//         <img src={whatsappIcon} className="h-8 mr-2" alt="WhatsApp" />
//         <span className="inline-block whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out">
//           Chat with us
//         </span>
//       </div>
//     </a>
//   );
// }
